import type { UsageEvent, UsageTracker, UsageTrackingConfig } from "./usageTracking.types";

const FLUSH_INTERVAL_MS = 10_000;
const FLUSH_THRESHOLD = 20;
const MAX_BATCH_SIZE = 50;
export const VISIT_TIMEOUT_MS = 30 * 60 * 1000;
const MAX_PAGE_LENGTH = 200;
// Shared by every tab of the browser: they all report the same visit.
export const VISIT_STORAGE_KEY = "yeati_usage_visit";
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

type SharedVisit = { token: string, lastEventAt: number };

type Outcome = "enabled" | "disabled" | "unauthorized" | "failed";

// Once the server says the switch is off, stay silent until the page is reloaded.
let disabledUntilReload = false;

// Storage errors and invalid values are ignored: the tab then keeps its visit in memory.
const readSharedVisit = (): SharedVisit | null => {
  try {
    const raw = window.localStorage.getItem(VISIT_STORAGE_KEY);
    if (!raw) return null;
    const value: unknown = JSON.parse(raw);
    if (!value || typeof value !== "object" || !("token" in value) || !("lastEventAt" in value)) return null;
    const { token, lastEventAt } = value;
    if (typeof token !== "string" || !UUID_PATTERN.test(token)) return null;
    if (typeof lastEventAt !== "number" || !Number.isFinite(lastEventAt)) return null;
    return { token, lastEventAt };
  }
  catch {
    return null;
  }
};

const writeSharedVisit = (visit: SharedVisit) => {
  try {
    window.localStorage.setItem(VISIT_STORAGE_KEY, JSON.stringify(visit));
  }
  catch {
    // Storage full or unavailable.
  }
};

const clearSharedVisit = () => {
  try {
    window.localStorage.removeItem(VISIT_STORAGE_KEY);
  }
  catch {
    // Storage unavailable.
  }
};

export const sanitizePage = (page: string): string => page.split(/[?#]/)[0].slice(0, MAX_PAGE_LENGTH);

export const createUsageTracker = ({
  apiBaseUrl, visitorToken, getAccessToken, appVersion,
}: UsageTrackingConfig): UsageTracker => {
  let active = false;
  let visitToken = "";
  let visitReady: Promise<boolean> = Promise.resolve(false);
  let visitEnabled = false;
  let queue: UsageEvent[] = [];
  let lastEventAt = 0;
  let lastPage: string | null = null;
  let timer: ReturnType<typeof setInterval> | undefined;

  // Errors are swallowed on purpose: one attempt, no toast, no Sentry, no token refresh.
  const post = async (path: string, body: unknown, keepalive = false): Promise<Outcome> => {
    const token = getAccessToken();
    if (!token) return "unauthorized";
    try {
      const response = await fetch(`${apiBaseUrl}/usage_tracking/${path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify(body),
        credentials: "omit",
        keepalive,
      });
      if (response.status === 401) return "unauthorized";
      if (!response.ok) return "failed";
      const data: unknown = await response.json();
      const enabled = data && typeof data === "object" && "enabled" in data ? data.enabled : null;
      if (enabled === false) return "disabled";
      return enabled === true ? "enabled" : "failed";
    }
    catch {
      return "failed";
    }
  };

  // Another tab may have extended the visit since this tab's last view.
  const isVisitExpired = () => {
    const shared = readSharedVisit();
    const lastActivityAt = shared?.token === visitToken ? Math.max(lastEventAt, shared.lastEventAt) : lastEventAt;
    return Date.now() - lastActivityAt > VISIT_TIMEOUT_MS;
  };

  // Never written once logged out, so the next login does not resume the previous user's visit.
  const saveSharedVisit = () => {
    if (getAccessToken()) writeSharedVisit({ token: visitToken, lastEventAt });
  };

  // The session is gone (logged out elsewhere): the next login must not resume this visit.
  const forgetSharedVisit = () => {
    if (readSharedVisit()?.token === visitToken) clearSharedVisit();
  };

  const onVisibilityChange = () => {
    if (document.visibilityState === "hidden") {
      flush(true);
      return;
    }
    if (isVisitExpired()) rotateVisit(true);
  };

  const onPageHide = () => flush(true);

  const shutdown = (permanent: boolean) => {
    if (permanent) disabledUntilReload = true;
    active = false;
    queue = [];
    clearInterval(timer);
    document.removeEventListener("visibilitychange", onVisibilityChange);
    window.removeEventListener("pagehide", onPageHide);
  };

  const handleOutcome = (outcome: Outcome): boolean => {
    if (outcome === "unauthorized") forgetSharedVisit();
    if (outcome === "disabled" || outcome === "unauthorized") shutdown(outcome === "disabled");
    return outcome === "enabled";
  };

  const flush = (keepalive = false) => {
    if (!active || queue.length === 0) return;
    // A page being hidden cannot wait for the visit acknowledgement.
    if (keepalive && !visitEnabled) return;
    const token = visitToken;
    const ready = visitReady;
    const batches: UsageEvent[][] = [];
    while (queue.length > 0) batches.push(queue.splice(0, MAX_BATCH_SIZE));
    const send = (events: UsageEvent[]) => post("events", { visit_token: token, visitor_token: visitorToken, events }, keepalive)
      .then(handleOutcome);
    if (keepalive) {
      batches.forEach((events) => void send(events));
      return;
    }
    void ready.then(async (enabled) => {
      if (!enabled) return;
      for (const events of batches) {
        if (!active) return;
        await send(events);
      }
    });
  };

  // Resumes the visit of another tab when it is still running, otherwise starts a new one.
  const startVisit = () => {
    const now = Date.now();
    const shared = readSharedVisit();
    const resumed = shared && now - shared.lastEventAt <= VISIT_TIMEOUT_MS ? shared : null;
    const token = resumed?.token ?? crypto.randomUUID();
    visitToken = token;
    visitEnabled = false;
    lastEventAt = resumed?.lastEventAt ?? now;
    if (!resumed) saveSharedVisit();
    visitReady = post("visits", {
      visit_token: token,
      visitor_token: visitorToken,
      app: "console",
      platform: "web",
      ...(appVersion ? { app_version: appVersion } : {}),
    }).then((outcome) => {
      if (outcome === "enabled") {
        if (token === visitToken) visitEnabled = true;
        return true;
      }
      // Without an acknowledged visit, events would be rejected: give up until next start.
      if (outcome === "unauthorized") forgetSharedVisit();
      shutdown(outcome === "disabled");
      return false;
    });
  };

  const enqueue = (page: string) => {
    queue.push({
      id: crypto.randomUUID(), name: "$view", properties: { page }, time: new Date().toISOString(),
    });
    lastEventAt = Date.now();
    saveSharedVisit();
    lastPage = page;
    if (queue.length >= FLUSH_THRESHOLD) flush();
  };

  const rotateVisit = (replayPage: boolean) => {
    flush();
    startVisit();
    if (replayPage && lastPage) enqueue(lastPage);
  };

  return {
    start: () => {
      if (active || disabledUntilReload) return;
      active = true;
      lastPage = null;
      startVisit();
      timer = setInterval(() => flush(), FLUSH_INTERVAL_MS);
      document.addEventListener("visibilitychange", onVisibilityChange);
      window.addEventListener("pagehide", onPageHide);
    },
    trackView: (page: string) => {
      if (!active) return;
      const cleanPage = sanitizePage(page);
      if (!cleanPage) return;
      if (isVisitExpired()) rotateVisit(false);
      if (cleanPage === lastPage) return;
      enqueue(cleanPage);
    },
    stop: () => {
      if (!active) return;
      flush(true);
      shutdown(false);
      clearSharedVisit();
    },
  };
};
